import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import ApplicationsPanel from "../admin/ApplicationsPanel";

const ONBOARDING_EVENT_COPY = {
  "onboarding.tp_approved": (e) => `TP approved: ${e.company_name || "a partner"} — portal invite sent`,
  "onboarding.tp_rejected": (e) => `TP rejected: ${e.company_name || "an applicant"}`,
  "onboarding.driver_approved": (e) => `Driver approved: ${e.driver_name || "a driver"} — beacon link sent`,
  "onboarding.driver_rejected": (e) => `Driver rejected: ${e.driver_name || "an applicant"}`,
};

/**
 * ApprovalsLane — TP/driver onboarding, approval, and credential issuance,
 * done right on the Floor instead of needing a trip to Admin. Embeds the
 * EXACT same <ApplicationsPanel/> Admin uses for approve/reject — one
 * place, one code path, whether a VA clicks it here, an admin clicks it
 * in Admin → Applications, or the Co-Pilot does it on command.
 *
 * "Issue / resend access" below covers the other half: an already-approved
 * TP or driver whose invite never arrived, or who needs a fresh link.
 */
export default function ApprovalsLane() {
  // Defaults to collapsed (2026-10-15 fix) — expanded by default was
  // pushing ProviderChips/Coworker off-screen and overcrowding the whole
  // left rail every time the Floor loaded. Still one click away via the
  // header, and the "N waiting" badge still shows without expanding.
  const [collapsed, setCollapsed] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [resendKind, setResendKind] = useState("tp");
  const [resendQuery, setResendQuery] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const seenAtRef = useRef(new Set());
  const sinceRef = useRef(new Date().toISOString()); // only toast events newer than mount — no backlog flood

  const refreshCount = useCallback(async () => {
    try {
      const [tpRes, drvRes] = await Promise.all([
        apiClient.get("/tpt/applications?state=pending"),
        apiClient.get("/tpt/drivers/new-applications"),
      ]);
      const tpRows = Array.isArray(tpRes.data) ? tpRes.data : [];
      const drvRows = Array.isArray(drvRes.data) ? drvRes.data : [];
      setPendingCount(tpRows.length + drvRows.length);
    } catch {
      // badge is a convenience, not a source of truth — ApplicationsPanel below still works either way
    }
  }, []);

  const pollTeamAlerts = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/dispatch/recent-events?limit=25");
      const rows = Array.isArray(data) ? data : [];
      for (const e of rows) {
        const copyFn = ONBOARDING_EVENT_COPY[e.kind];
        if (!copyFn || !e.at) continue;
        if (e.at <= sinceRef.current) continue;
        const key = `${e.kind}:${e.at}`;
        if (seenAtRef.current.has(key)) continue;
        seenAtRef.current.add(key);
        toast.info(copyFn(e), { duration: 6000 });
      }
    } catch {
      // team alert toasts are a convenience — never block the lane on this
    }
  }, []);

  useEffect(() => {
    refreshCount();
    pollTeamAlerts();
    const t = setInterval(refreshCount, 20000);
    const t2 = setInterval(pollTeamAlerts, 12000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, [refreshCount, pollTeamAlerts]);

  const issueAccess = async () => {
    const q = resendQuery.trim();
    if (!q) { toast.error(`Enter a ${resendKind === "tp" ? "TP" : "driver"} ID`); return; }
    setResendBusy(true);
    try {
      if (resendKind === "tp") {
        const { data } = await apiClient.post(`/tpt/${q}/issue-portal`);
        toast.success(`Portal access ${data.reissued ? "reissued" : "issued"} — invite sent to ${data.dispatcher_email}`);
      } else {
        await apiClient.post(`/tpt/drivers/${q}/beacon-sms`);
        toast.success("Beacon link (re)sent");
      }
      setResendQuery("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue access — check the ID");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <aside className={`kil-panel ${collapsed ? "kil-panel--collapsed" : ""}`} data-testid="approvals-lane">
      <div className="kil-head" onClick={() => setCollapsed(c => !c)} style={{ cursor: "pointer" }} data-testid="approvals-lane-toggle">
        <div className="kil-title">
          <span className="kil-dot" />
          <span className="kil-label">ONBOARDING &amp; APPROVALS</span>
        </div>
        {pendingCount > 0 && (
          <span className="kil-count" data-testid="approvals-pending-badge">{pendingCount} waiting</span>
        )}
      </div>
      {!collapsed && (
        <div style={{ padding: "6px 10px 10px" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 10, maxHeight: 360, overflowY: "auto" }}>
            <ApplicationsPanel embedded onApproved={refreshCount} />
          </div>

          <div className="apl-issue-box" data-testid="approvals-issue-box">
            <div className="kil-label" style={{ marginBottom: 6 }}>ISSUE / RESEND ACCESS</div>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                value={resendKind}
                onChange={(e) => setResendKind(e.target.value)}
                className="apl-issue-select"
                data-testid="approvals-issue-kind"
              >
                <option value="tp">TP portal</option>
                <option value="driver">Driver beacon</option>
              </select>
              <input
                value={resendQuery}
                onChange={(e) => setResendQuery(e.target.value)}
                placeholder={resendKind === "tp" ? "TP id" : "Driver id"}
                className="apl-issue-input"
                data-testid="approvals-issue-id-input"
              />
              <button
                onClick={issueAccess}
                disabled={resendBusy}
                className="apl-issue-btn"
                data-testid="approvals-issue-submit"
              >
                {resendBusy ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
